import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true, // send session cookie for auth
});

// Attach JWT (admin) token from localStorage if present
apiClient.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('emerald_token') : null;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// -------- Existing helpers --------
export const fetchQuotes = async () => {
  const { data } = await apiClient.get('/market/quotes');
  return data.data || [];
};

export const fetchNews = async (category = 'all', limit = 15) => {
  const { data } = await apiClient.get('/news', { params: { category, limit } });
  return data.data || [];
};

export const subscribeEmail = async (email) => {
  const { data } = await apiClient.post('/subscribe', { email });
  return data;
};

export const submitContact = async (payload) => {
  const { data } = await apiClient.post('/contact', payload);
  return data;
};

export const fetchSubscribers = async () => {
  const { data } = await apiClient.get('/subscribers');
  return data;
};

export const fetchContacts = async () => {
  const { data } = await apiClient.get('/contacts');
  return data;
};

// -------- Auth --------
export const authLogin = async (identifier, password) => {
  const { data } = await apiClient.post('/auth/login', { identifier, password });
  if (data?.token) window.localStorage.setItem('emerald_token', data.token);
  return data;
};

export const authSetPassword = async (token, password) => {
  const { data } = await apiClient.post('/auth/set-password', { token, password });
  if (data?.token) window.localStorage.setItem('emerald_token', data.token);
  return data;
};

export const authForgotPassword = async (email) => {
  const { data } = await apiClient.post('/auth/forgot-password', { email });
  return data;
};

export const authResetPassword = async (token, password) => {
  const { data } = await apiClient.post('/auth/reset-password', { token, password });
  if (data?.token) window.localStorage.setItem('emerald_token', data.token);
  return data;
};

export const authGoogleSession = async (sessionId) => {
  const { data } = await apiClient.post('/auth/session', { session_id: sessionId });
  if (data?.token) window.localStorage.setItem('emerald_token', data.token);
  return data;
};

export const authMe = async () => {
  const { data } = await apiClient.get('/auth/me');
  return data;
};

export const authLogout = async () => {
  try {
    await apiClient.post('/auth/logout');
  } finally {
    window.localStorage.removeItem('emerald_token');
  }
};

// -------- Users --------
export const listUsers = async () => (await apiClient.get('/users/directory')).data;
export const listAllUsersAdmin = async () => (await apiClient.get('/users')).data;
export const adminResetUserPassword = async (uid) => (await apiClient.post(`/users/${uid}/reset-password`)).data;
export const changePassword = async (current_password, new_password) =>
  (await apiClient.post('/auth/change-password', { current_password, new_password })).data;
export const patchMyProfile = async (patch) => (await apiClient.patch('/auth/me', patch)).data;
export const requestOwnReset = async (email) =>
  (await apiClient.post('/auth/forgot-password', { email })).data;
export const createUser = async (payload) => (await apiClient.post('/users', payload)).data;
export const bulkImportUsers = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await apiClient.post('/users/bulk-import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};
export const bulkImportTemplateUrl = () => `${apiClient.defaults.baseURL}/users/bulk-import-template.csv`;
export const updateUser = async (id, payload) => (await apiClient.patch(`/users/${id}`, payload)).data;
export const deleteUser = async (id) => (await apiClient.delete(`/users/${id}`)).data;
export const resendInvite = async (id) => (await apiClient.post(`/users/${id}/resend-invite`)).data;

// -------- Verticals --------
export const listVerticals = async () => (await apiClient.get('/verticals')).data;
export const getVertical = async (slug) => (await apiClient.get(`/verticals/${slug}`)).data;
export const createVertical = async (payload) => (await apiClient.post('/verticals', payload)).data;
export const updateVertical = async (id, payload) => (await apiClient.patch(`/verticals/${id}`, payload)).data;
export const deleteVertical = async (id) => (await apiClient.delete(`/verticals/${id}`)).data;

// -------- Company News --------
export const listCompanyNews = async (limit = 30) => (await apiClient.get('/company-news', { params: { limit } })).data;
export const getCompanyNews = async (slug) => (await apiClient.get(`/company-news/${slug}`)).data;
export const createCompanyNews = async (payload) => (await apiClient.post('/company-news', payload)).data;
export const updateCompanyNews = async (id, payload) => (await apiClient.patch(`/company-news/${id}`, payload)).data;
export const deleteCompanyNews = async (id) => (await apiClient.delete(`/company-news/${id}`)).data;
export const broadcastCompanyNews = async (id) => (await apiClient.post(`/company-news/${id}/broadcast`)).data;

// Daily Sayings
export const listSayings = async (limit = 30) => (await apiClient.get('/sayings', { params: { limit } })).data;
export const getSaying = async (slug) => (await apiClient.get(`/sayings/${slug}`)).data;
export const createSaying = async (payload) => (await apiClient.post('/sayings', payload)).data;
export const updateSaying = async (id, payload) => (await apiClient.patch(`/sayings/${id}`, payload)).data;
export const deleteSaying = async (id) => (await apiClient.delete(`/sayings/${id}`)).data;
export const broadcastSaying = async (id) => (await apiClient.post(`/sayings/${id}/broadcast`)).data;
export const zapierTest = async () => (await apiClient.post('/company-news/webhook/test')).data;
export const zapierLog = async (limit = 20) => (await apiClient.get('/company-news/webhook/log', { params: { limit } })).data;

// -------- About Page CMS --------
export const getAbout = async () => (await apiClient.get('/about')).data;
export const saveAbout = async (payload) => (await apiClient.put('/about', payload)).data;

// -------- Homepage Layout CMS --------
export const getHomeLayout = async () => (await apiClient.get('/home-layout')).data;
export const saveHomeLayout = async (payload) => (await apiClient.put('/home-layout', payload)).data;

// -------- People (Personnel) --------
export const listPeople = async (category) => (await apiClient.get('/people', { params: category ? { category } : {} })).data;
export const getPerson = async (slug) => (await apiClient.get(`/people/${slug}`)).data;
export const createPerson = async (payload) => (await apiClient.post('/people', payload)).data;
export const updatePerson = async (id, payload) => (await apiClient.patch(`/people/${id}`, payload)).data;
export const deletePerson = async (id) => (await apiClient.delete(`/people/${id}`)).data;

// -------- Personnel Blog --------
export const listPersonBlog = async (slug) => (await apiClient.get(`/people/${slug}/blog`)).data;
export const getPersonBlogPost = async (personSlug, postSlug) => (await apiClient.get(`/people/${personSlug}/blog/${postSlug}`)).data;
export const createBlogPost = async (personId, payload) => (await apiClient.post(`/people/${personId}/blog`, payload)).data;
export const updateBlogPost = async (personId, postId, payload) => (await apiClient.patch(`/people/${personId}/blog/${postId}`, payload)).data;
export const deleteBlogPost = async (personId, postId) => (await apiClient.delete(`/people/${personId}/blog/${postId}`)).data;

// -------- CRM --------
// -------- CRM · Broadcasts --------
export const listBroadcasts = async () => (await apiClient.get('/crm/broadcasts')).data;
export const createBroadcast = async (payload) => (await apiClient.post('/crm/broadcasts', payload)).data;
export const getBroadcast = async (id) => (await apiClient.get(`/crm/broadcasts/${id}`)).data;
export const getBroadcastReads = async (id) => (await apiClient.get(`/crm/broadcasts/${id}/reads`)).data;
export const deleteBroadcast = async (id) => (await apiClient.delete(`/crm/broadcasts/${id}`)).data;

// -------- CRM · Inbox --------
export const listCrmInbox = async () => (await apiClient.get('/crm/inbox')).data;
export const markInboxRead = async (id) => (await apiClient.post(`/crm/inbox/${id}/read`)).data;
export const markAllInboxRead = async () => (await apiClient.post('/crm/inbox/read-all')).data;

// -------- Global Search --------
export const globalSearch = async (q) => (await apiClient.get('/search', { params: { q } })).data;

// -------- CRM · Follow-up + Task history --------
export const followUpTicket = async (ticketId, note = '', sendEmail = true) =>
  (await apiClient.post(`/crm/tickets/${ticketId}/followup`, { note, send_email: sendEmail })).data;
export const taskHistory = async (taskId) => (await apiClient.get(`/crm/tasks/${taskId}/history`)).data;

export const crmStats = async () => (await apiClient.get('/crm/stats')).data;
export const crmMyWork = async () => (await apiClient.get('/crm/my-work')).data;

// -------- CRM · Docs (rich-text pages attached to tickets) --------
export const listDocs = async (ticketId) => (await apiClient.get(`/crm/tickets/${ticketId}/docs`)).data;
export const getDoc = async (docId) => (await apiClient.get(`/crm/docs/${docId}`)).data;
export const createDoc = async (payload) => (await apiClient.post('/crm/docs', payload)).data;
export const updateDoc = async (docId, payload) => (await apiClient.patch(`/crm/docs/${docId}`, payload)).data;
export const deleteDoc = async (docId) => (await apiClient.delete(`/crm/docs/${docId}`)).data;
export const runDigest = async (userId) => (await apiClient.post(`/crm/digest/run${userId ? `?user_id=${userId}` : ''}`)).data;
export const listDepartments = async () => (await apiClient.get('/crm/departments')).data;
export const createDepartment = async (p) => (await apiClient.post('/crm/departments', p)).data;
export const updateDepartment = async (id, p) => (await apiClient.patch(`/crm/departments/${id}`, p)).data;
export const deleteDepartment = async (id) => (await apiClient.delete(`/crm/departments/${id}`)).data;

export const listTickets = async (params) => (await apiClient.get('/crm/tickets', { params })).data;
export const getTicket = async (id) => (await apiClient.get(`/crm/tickets/${id}`)).data;
export const createTicket = async (p) => (await apiClient.post('/crm/tickets', p)).data;
export const updateTicket = async (id, p) => (await apiClient.patch(`/crm/tickets/${id}`, p)).data;
export const deleteTicket = async (id) => (await apiClient.delete(`/crm/tickets/${id}`)).data;

export const listTasks = async (params) => (await apiClient.get('/crm/tasks', { params })).data;
export const createTask = async (p) => (await apiClient.post('/crm/tasks', p)).data;
export const updateTask = async (id, p) => (await apiClient.patch(`/crm/tasks/${id}`, p)).data;
export const deleteTask = async (id) => (await apiClient.delete(`/crm/tasks/${id}`)).data;
export const getTaskDetail = async (id) => (await apiClient.get(`/crm/tasks/${id}`)).data;
export const reassignTask = async (id, p) => (await apiClient.post(`/crm/tasks/${id}/reassign`, p)).data;
export const acceptTask = async (id) => (await apiClient.post(`/crm/tasks/${id}/accept`)).data;
export const approveTask = async (id) => (await apiClient.post(`/crm/tasks/${id}/approve`)).data;
export const rejectTask = async (id, body) => (await apiClient.post(`/crm/tasks/${id}/reject`, { body })).data;
export const listTaskComments = async (id) => (await apiClient.get(`/crm/tasks/${id}/comments`)).data;
export const addTaskComment = async (id, body, opts = {}) => (await apiClient.post(`/crm/tasks/${id}/comments`, { body, ...opts })).data;
export const deleteTaskComment = async (id, cid) => (await apiClient.delete(`/crm/tasks/${id}/comments/${cid}`)).data;
export const listTaskAttachments = async (id) => (await apiClient.get(`/crm/tasks/${id}/attachments`)).data;
export const addTaskAttachment = async (id, p) => (await apiClient.post(`/crm/tasks/${id}/attachments`, p)).data;
export const deleteTaskAttachment = async (id, aid) => (await apiClient.delete(`/crm/tasks/${id}/attachments/${aid}`)).data;

export const listMessages = async (ticketId) => (await apiClient.get(`/crm/tickets/${ticketId}/messages`)).data;
export const createMessage = async (p) => (await apiClient.post('/crm/messages', p)).data;
export const markMessagesRead = async (ticketId) => (await apiClient.post(`/crm/tickets/${ticketId}/messages/mark-read`)).data;
export const markTaskCommentsRead = async (taskId) => (await apiClient.post(`/crm/tasks/${taskId}/comments/mark-read`)).data;
export const setTyping = async (ticketId) => (await apiClient.post(`/crm/tickets/${ticketId}/typing`)).data;
export const getTyping = async (ticketId) => (await apiClient.get(`/crm/tickets/${ticketId}/typing`)).data;

// Inactivity nudge settings
export const getNudgeSettings = async () => (await apiClient.get('/crm/settings/nudge')).data;
export const putNudgeSettings = async (p) => (await apiClient.put('/crm/settings/nudge', p)).data;
export const runNudgeNow = async () => (await apiClient.post('/crm/settings/nudge/run-now')).data;
export const deleteMessage = async (id) => (await apiClient.delete(`/crm/messages/${id}`)).data;

export const listCrmFiles = async (ticketId) => (await apiClient.get(`/crm/tickets/${ticketId}/files`)).data;
export const listCrmFilesTree = async (ticketId) => (await apiClient.get(`/crm/tickets/${ticketId}/files/tree`)).data;
export const listFileVersions = async (fileId) => (await apiClient.get(`/crm/files/${fileId}/versions`)).data;
export const createCrmFile = async (p) => (await apiClient.post('/crm/files', p)).data;
export const deleteCrmFile = async (id) => (await apiClient.delete(`/crm/files/${id}`)).data;
export const listFileComments = async (fileId) => (await apiClient.get(`/crm/files/${fileId}/comments`)).data;
export const createFileComment = async (p) => (await apiClient.post('/crm/file-comments', p)).data;

// -------- Masters: Job Titles --------
export const listJobTitles = async () => (await apiClient.get('/masters/job-titles')).data;
export const createJobTitle = async (p) => (await apiClient.post('/masters/job-titles', p)).data;
export const updateJobTitle = async (id, p) => (await apiClient.patch(`/masters/job-titles/${id}`, p)).data;
export const deleteJobTitle = async (id) => (await apiClient.delete(`/masters/job-titles/${id}`)).data;

// -------- Masters: Permission Overrides --------
export const getPermissionCatalog = async () => (await apiClient.get('/masters/permissions/catalog')).data;
export const getUserPermissions = async (userId) => (await apiClient.get(`/masters/permissions/user/${userId}`)).data;
export const setUserPermissions = async (userId, permissions) => (await apiClient.put(`/masters/permissions/user/${userId}`, { permissions })).data;
export const clearUserPermissions = async (userId) => (await apiClient.delete(`/masters/permissions/user/${userId}`)).data;

// -------- Galleries --------
export const listGalleries = async () => (await apiClient.get('/galleries')).data;
export const createGallery = async (p) => (await apiClient.post('/galleries', p)).data;
export const updateGallery = async (gid, p) => (await apiClient.patch(`/galleries/${gid}`, p)).data;
export const deleteGallery = async (gid) => (await apiClient.delete(`/galleries/${gid}`)).data;
export const getGallery = async (gid) => (await apiClient.get(`/galleries/${gid}`)).data;
export const addGalleryItem = async (gid, p) => (await apiClient.post(`/galleries/${gid}/items`, p)).data;
export const deleteGalleryItem = async (gid, itemId) => (await apiClient.delete(`/galleries/${gid}/items/${itemId}`)).data;
export const reorderGalleryItems = async (gid, itemIds) => (await apiClient.post(`/galleries/${gid}/reorder`, { item_ids: itemIds })).data;

// -------- Uploads --------
export const uploadFile = async (file, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post('/uploads', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
    },
  });
  return data;
};

// ─── Site settings (contact) ──────────────────────────────
export const getContactSettings = async () =>
  (await apiClient.get('/site-settings/contact')).data;
export const updateContactSettings = async (payload) =>
  (await apiClient.put('/site-settings/contact', payload)).data;

// Resolve a file URL that may be relative like /api/files/xxx
export const resolveFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

// -------- CRM · Leads --------
export const listLeads = async (params) => (await apiClient.get('/crm/leads', { params })).data;
export const getLead = async (id) => (await apiClient.get(`/crm/leads/${id}`)).data;
export const createLead = async (p) => (await apiClient.post('/crm/leads', p)).data;
export const updateLead = async (id, p) => (await apiClient.patch(`/crm/leads/${id}`, p)).data;
export const deleteLead = async (id) => (await apiClient.delete(`/crm/leads/${id}`)).data;
export const leadStats = async () => (await apiClient.get('/crm/leads/stats')).data;
export const addLeadNote = async (id, body) => (await apiClient.post(`/crm/leads/${id}/notes`, { body })).data;
export const listLeadActivities = async (id) => (await apiClient.get(`/crm/leads/${id}/activities`)).data;
export const addLeadFollowup = async (id, p) => (await apiClient.post(`/crm/leads/${id}/followups`, p)).data;
export const updateLeadFollowup = async (id, fuId, p) => (await apiClient.patch(`/crm/leads/${id}/followups/${fuId}`, p)).data;
export const deleteLeadFollowup = async (id, fuId) => (await apiClient.delete(`/crm/leads/${id}/followups/${fuId}`)).data;
export const addLeadFile = async (id, p) => (await apiClient.post(`/crm/leads/${id}/files`, p)).data;
export const deleteLeadFile = async (id, fileId) => (await apiClient.delete(`/crm/leads/${id}/files/${fileId}`)).data;
export const parseLeadsCsv = async (rawCsv) =>
  (await apiClient.post('/crm/leads/import/parse-csv', rawCsv, { headers: { 'Content-Type': 'text/csv' } })).data;
export const importLeads = async (payload) => (await apiClient.post('/crm/leads/import', payload)).data;
export const convertLeadToTicket = async (id, p) => (await apiClient.post(`/crm/leads/${id}/convert-to-ticket`, p)).data;
export const leadsCsvTemplateUrl = `${API}/crm/leads/import/template`;
export const leadsCsvExportUrl = (stage) => `${API}/crm/leads/export/csv${stage ? `?stage=${stage}` : ''}`;

// -------- CRM · Ratings --------
export const rateTask = async (taskId, score, comment = '') =>
  (await apiClient.post(`/crm/tasks/${taskId}/rate`, { score, comment })).data;
export const listTaskRatings = async (taskId) => (await apiClient.get(`/crm/tasks/${taskId}/ratings`)).data;
export const userRatingSummary = async (userId) => (await apiClient.get(`/crm/users/${userId}/ratings`)).data;

// -------- CRM · Calendar --------
export const listCalendarEvents = async (params) => (await apiClient.get('/crm/calendar/events', { params })).data;
export const calendarIcalUrl = (assigneeId) => `${API}/crm/calendar/export.ics${assigneeId ? `?assignee_id=${assigneeId}` : ''}`;

// -------- CRM · Office (My Day / Workload / Claim / Snooze / Time / Ticket rating) --------
export const getPriorityWeights = async () => (await apiClient.get('/crm/settings/priority-weights')).data;
export const setPriorityWeights = async (p) => (await apiClient.put('/crm/settings/priority-weights', p)).data;
export const claimTask = async (id) => (await apiClient.post(`/crm/tasks/${id}/claim`)).data;
export const requestSnooze = async (id, p) => (await apiClient.post(`/crm/tasks/${id}/snooze-request`, p)).data;
export const decideSnooze = async (id, p) => (await apiClient.post(`/crm/tasks/${id}/snooze-decide`, p)).data;
export const startTimer = async (id, note = '') => (await apiClient.post(`/crm/tasks/${id}/timer/start`, { note })).data;
export const stopTimer = async (id) => (await apiClient.post(`/crm/tasks/${id}/timer/stop`)).data;
export const logTime = async (id, p) => (await apiClient.post(`/crm/tasks/${id}/time-logs`, p)).data;
export const myDay = async () => (await apiClient.get('/crm/my-day')).data;
export const crmWorkload = async (params) => (await apiClient.get('/crm/workload', { params })).data;
export const rateTicket = async (id, score, comment = '') => (await apiClient.post(`/crm/tickets/${id}/rate`, { score, comment })).data;
export const listTicketRatings = async (id) => (await apiClient.get(`/crm/tickets/${id}/ratings`)).data;
